import type { YardEvent } from "@/types/shared/yardEvent";
import type { WebSocketMessageStatus } from "@/services/websocket/types";

// Equipment related types
export interface VisitEquipmentIdentifier {
  version?: string;
  id?: string;
}

/**
 * Interface for TDR handover details
 */
export interface TdrHandoverDetails {
  tdrState: string;
  tdrStateTimeStamp: number;
  [key: string]: any;
}

/**
 * Interface for TDR detail with equipment ID
 */
export interface TdrDetail {
  equipmentId: string;
  tdrHandoverDetails: TdrHandoverDetails;
}

export interface VisitLicensePlate {
  licensePlateNumber?: string;
  countryCode?: string;
  subnationalEntity?: string;
}

export interface EquipmentTypeDetails {
  expectedNumberOfSeals: number;
  movesByItself: boolean;
  loadable: boolean;
}

export interface EquipmentDetails {
  identifier?: VisitEquipmentIdentifier;
  licensePlate?: VisitLicensePlate;
  type?: string;
  ownerCode?: string;
  fleetSystemId?: string;
  typeDetails?: EquipmentTypeDetails;
  equipmentNumber?: string;
}

export interface VisitId {
  id?: string;
}

export interface VisitStatus {
  type?: string;
}

export interface LoadIntegrityIssue {
  riskType: string;
}

export interface VrHaulIdentifiers {
  vrId?: string;
  locationPlanId?: string;
}

export interface HaulIdentifier {
  vrHaulIdentifiers?: VrHaulIdentifiers;
  isaId?: string;
}

export interface VisitReason {
  drop: boolean;
  nonInventory: boolean;
  dsp: boolean | null;
  isLiveLoad: boolean | null;
  isPick: boolean;
  isRelay: boolean;
  isDrop: boolean;
  /** Current haul action used to resolve inbound/outbound queue direction. */
  haulActionType?: string;
  relay: boolean;
  isPendingAssistance: boolean;
  passThrough: boolean;
  isNonInventory: boolean;
  loadIntegrityIssueList?: LoadIntegrityIssue[];
  haulIdentifier: HaulIdentifier;
  /** Lane routing, e.g. "BFI4->BFI1". Absent on many visits. */
  facilityLane?: string;
  /**
   * SortTech load-plan execution status (e.g. "SCHEDULED",
   * "LOADING_IN_PROGRESS", "LOADING_PAUSED", "LOADED"), surfaced via the DAS
   * visit augmentation. Absent on visits with no associated load plan.
   */
  loadPlanStatus?: string;
  shipperAccounts?: string;
  [key: string]: any;
}

export interface VisitDetails {
  originalVisitReasons: VisitReason[];
  activeVisitReasons: VisitReason[];
  visitVersion: number;
  visitId: VisitId;
  visitStatus: VisitStatus;
}

/**
 * Flatten the per-reason `loadIntegrityIssueList` into a single array of
 * issues for the visit. Backend payload nests these one level deep at
 * `equipment.visitDetails.activeVisitReasons[*].loadIntegrityIssueList`;
 * call sites pass the reasons array straight through.
 *
 * Accepts the loose `Record<string, unknown>` shape that some extractors
 * carry (the SDK types `data` as opaque `__DocumentType` so upstream
 * doesn't always have `VisitReason[]` in hand). The runtime guard
 * tolerates malformed payloads where `loadIntegrityIssueList` is null,
 * undefined, or non-array.
 */
export const parseLoadIntegrityIssues = (
  reasons: ReadonlyArray<Record<string, unknown>> | undefined
): LoadIntegrityIssue[] =>
  (reasons ?? []).flatMap((reason) => {
    if (!reason) return [];
    const list = reason.loadIntegrityIssueList;
    return Array.isArray(list) ? (list as LoadIntegrityIssue[]) : [];
  });

export interface ContentTag {
  type?: string;
  subType?: string;
  condition?: string;
  purpose?: string;
}

export interface ContentMetadata {
  contentTags?: ContentTag[];
}

export interface Equipment {
  equipmentDetails: EquipmentDetails;
  visitDetails: VisitDetails;
  tdrHandoverDetails: TdrHandoverDetails;
  contentMetadata?: ContentMetadata;
  groupId?: string | null;
  operator?: string | null;
  fleetSystemName?: string;
  verificationStatus?: string;
  /**
   * Epoch ms the trailer physically arrived at its location. RYTE compares
   * the load/unload goal's visit `arrivalAtLocationUTC` against that goal's
   * `executionWindow.delayedAfter` to show early/late/on-time. Already read
   * untyped by RYC (`computeOperationalActivityData`); declared here for
   * type-safety.
   */
  arrivalAtLocationUTC?: number | null;
  [key: string]: any;
}

// Location related types
export interface LocationIdentifier {
  locationCode: string;
  id: string;
}

export interface Location {
  identifier: LocationIdentifier;
  zone?: string | null;
  label: string;
  type: string;
  zones: string[];
  multiCapacity: boolean;
  capacity: number;
}

// Visit Data structure matching API response
export interface VisitData {
  equipment: Equipment;
  location: Location;
  isDisplaced: boolean | null;
  isInTransit: boolean | null;
  isYardFleet?: boolean | null;
  events?: YardEvent[];
  [key: string]: any;
}

// Base Visit interface matching API response format
export interface Visit {
  data: VisitData;
  visitId: string;
  yardId: string;
}

// YardVisit interface for internal use
export interface YardVisit extends Visit {
  locationCode: string;
  datapathViewVersion?: string;
}

export interface WebsocketVisitMessage {
  /**
   * The visit id, from the DAS envelope. Present on every frame (paired with
   * `sortType: "visitId"`) — it is the canonical id and the only field a
   * DELETE marker carries, since those arrive without a `record`.
   */
  sortId: string;
  /** Absent on DELETE markers; present on UPDATE frames. */
  record?: YardVisit;
  metadata?: { status?: WebSocketMessageStatus };
  [key: string]: any;
}

/**
 * Canonical yard-location type enum. Values are the strings the backend
 * uses on `visit.data.location.type` and `yardMap.locations[].type`.
 *
 * The page-layer `LocationTypeFilterValue` (in `equipmentFilters.ts`) is
 * an intentional **subset** of this — the equipment filter UI doesn't
 * expose `EXIT_GATE` because users can't filter by it. Don't unify them;
 * one is "domain values", the other is "filter options ⊆ domain".
 */
export enum YardLocationType {
  DOCK_DOOR = "DockDoor",
  PARKING_SLIP = "ParkingSlip",
  OFF_SITE = "OffSite",
  ENTRY_GATE = "EntryGate",
  EXIT_GATE = "ExitGate",
}

/**
 * `visit.data.equipment.rejectionDetails.rejectionState` values for RYC
 * visits. Distinct from `YardEventType.REJECT_CHECK_IN` / `_OUT` (which
 * describe historical events) — these describe a visit's CURRENT state.
 */
export enum VisitRejectionState {
  REJECT_CHECK_IN = "REJECT_CHECK_IN",
  REJECT_CHECK_OUT = "REJECT_CHECK_OUT",
}
